const ONE_MB = 1024 * 1024;

export const PROFILE_UPLOAD_OPTIONS = {
  maxSizeBytes: 350 * 1024,
  maxDimension: 1200,
  quality: 0.75,
};

export const DOCUMENT_UPLOAD_OPTIONS = {
  maxSizeBytes: 800 * 1024,
  maxDimension: 1600,
  quality: 0.8,
};

export const MAX_REGISTRATION_UPLOAD_BYTES = 4 * ONE_MB;

const IMAGE_MIME_TYPES = new Set([
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
]);

const loadImage = (source) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = source;
  });

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

export const fileToDataUrl = blobToDataUrl;

const getResizedDimensions = (width, height, maxDimension) => {
  if (!maxDimension || (width <= maxDimension && height <= maxDimension)) {
    return { width, height };
  }

  const ratio = Math.min(maxDimension / width, maxDimension / height);

  return {
    width: Math.round(width * ratio),
    height: Math.round(height * ratio),
  };
};

const canvasToBlob = (canvas, type, quality) =>
  new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error("Unable to prepare the selected file."));
        return;
      }
      resolve(blob);
    }, type, quality);
  });

const compressImageFile = async (file, options) => {
  const objectUrl = URL.createObjectURL(file);

  try {
    const image = await loadImage(objectUrl);
    const { width, height } = getResizedDimensions(
      image.width,
      image.height,
      options.maxDimension
    );

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext("2d");
    context.drawImage(image, 0, 0, width, height);

    let quality = options.quality ?? 0.8;
    let outputType = file.type === "image/png" ? "image/png" : "image/jpeg";
    let blob = await canvasToBlob(canvas, outputType, quality);

    while (blob.size > options.maxSizeBytes && quality > 0.45) {
      quality -= 0.1;
      outputType = "image/jpeg";
      blob = await canvasToBlob(canvas, outputType, quality);
    }

    if (blob.size > options.maxSizeBytes) {
      throw new Error(
        `Please use a smaller image. The maximum allowed size is ${Math.round(
          options.maxSizeBytes / 1024
        )} KB after compression.`
      );
    }

    const extension = outputType === "image/png" ? "png" : "jpg";
    const baseName = file.name.replace(/\.[^.]+$/, "") || "upload";

    return new File([blob], `${baseName}.${extension}`, {
      type: outputType,
      lastModified: Date.now(),
    });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

export const prepareUploadFile = async (file, options) => {
  if (!file) {
    return null;
  }

  if (!IMAGE_MIME_TYPES.has(file.type)) {
    if (file.size > options.maxSizeBytes) {
      throw new Error(
        `Please use a smaller file. The maximum allowed size is ${Math.round(
          options.maxSizeBytes / 1024
        )} KB.`
      );
    }

    return file;
  }

  if (
    file.size <= options.maxSizeBytes &&
    (!options.maxDimension || options.maxDimension <= 0)
  ) {
    return file;
  }

  return compressImageFile(file, options);
};